"use client";

import { useLayoutEffect, useState } from "react";

/** Matches `use-responsive-widget-position` / legacy Skinny mobile breakpoint. */
export const WIDGET_MOBILE_MQ = "(max-width: 575.98px)";

export type WidgetVisualViewportLayout = {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
};

function readIsMobileViewport(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(WIDGET_MOBILE_MQ).matches;
}

/** Synchronous first paint + `matchMedia` updates for the widget mobile breakpoint. */
export function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = useState(readIsMobileViewport);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mq = window.matchMedia(WIDGET_MOBILE_MQ);
    const apply = () => {
      setIsMobile(mq.matches);
    };

    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  return isMobile;
}

export function readWidgetVisualViewportLayout(): WidgetVisualViewportLayout {
  if (typeof window === "undefined") {
    return { top: 0, left: 0, width: 0, height: 0 };
  }

  const vv = window.visualViewport;
  if (vv) {
    return {
      top: vv.offsetTop,
      left: vv.offsetLeft,
      width: vv.width,
      height: vv.height,
    };
  }

  return {
    top: 0,
    left: 0,
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

/**
 * Tracks mobile breakpoint + visual viewport geometry while the floating panel is
 * open. iOS Safari shrinks `visualViewport` when the soft keyboard or URL bar
 * chrome appears — raw `100vh` on a fixed corner card does not follow it (#1154).
 */
export function useWidgetMobileLayout(panelOpen: boolean): {
  readonly isMobile: boolean;
  readonly viewport: WidgetVisualViewportLayout | null;
} {
  const isMobile = useIsMobileViewport();
  const [viewport, setViewport] = useState<WidgetVisualViewportLayout | null>(null);

  useLayoutEffect(() => {
    if (!panelOpen || !isMobile || typeof window === "undefined") {
      return undefined;
    }

    const sync = () => {
      setViewport(readWidgetVisualViewportLayout());
    };

    sync();
    const vv = window.visualViewport;
    vv?.addEventListener("resize", sync);
    vv?.addEventListener("scroll", sync);
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);

    return () => {
      vv?.removeEventListener("resize", sync);
      vv?.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
    };
  }, [isMobile, panelOpen]);

  return {
    isMobile,
    viewport: panelOpen && isMobile ? viewport : null,
  };
}
